import {computed, onMounted, onUnmounted, ref, shallowRef, watch} from 'vue'

import type {BellowsDirection} from '@/components/harmonika'
import {midiToFreq, PRECALC_DATA} from '@/components/harmonika'

import {AudioEngine} from './audio/AudioEngine'

const SCHWEBUNG_HZ = 4
const MAX_NOTES_PER_BUTTON = 3

function sameNumberSet(left: Set<number>, right: Set<number>) {
  if (left.size !== right.size) return false

  for (const item of right) {
    if (!left.has(item)) return false
  }

  return true
}

/** Zerlegt eine Knopf-ID `"row-btn"` sicher in ein Indexpaar (null bei Fehlformat). */
function parseButtonId(buttonId: string): [number, number] | null {
  const [rowIdx, btnIdx] = buttonId.split('-').map(Number)
  if (rowIdx === undefined || btnIdx === undefined || Number.isNaN(rowIdx) || Number.isNaN(btnIdx)) {
    return null
  }
  return [rowIdx, btnIdx]
}

/**
 * Manages Harmonika audio playback and active button state.
 */
export function useHarmonikaAudio() {
  const audioEngine = shallowRef<AudioEngine | null>(null)
  const direction = ref<BellowsDirection>('DRUCK')
  const activeButtons = ref<Set<string>>(new Set())

  const activeMidiNotes = computed<Set<number>>((oldValue) => {
    const activeMidis = new Set<number>()

    activeButtons.value.forEach((buttonId) => {
      const parsed = parseButtonId(buttonId)
      if (!parsed) return
      const [rowIdx, btnIdx] = parsed
      const buttonData = PRECALC_DATA[rowIdx]?.buttons[btnIdx]
      if (!buttonData) return

      const notes = direction.value === 'DRUCK' ? buttonData.druckMidis : buttonData.zugMidis
      notes.forEach((midi) => activeMidis.add(midi))
    })

    return oldValue && sameNumberSet(oldValue, activeMidis) ? oldValue : activeMidis
  })

  function playButtonNotes(rowIdx: number, btnIdx: number, currentDirection: BellowsDirection) {
    const buttonData = PRECALC_DATA[rowIdx]?.buttons[btnIdx]
    if (!buttonData) return

    const notesToPlay = currentDirection === 'DRUCK' ? buttonData.druckMidis : buttonData.zugMidis
    const buttonId = `${rowIdx}-${btnIdx}`

    notesToPlay.forEach((midi, index) => {
      const frequency = midiToFreq(midi)
      audioEngine.value?.playNote(frequency, `${buttonId}-${index}`)
      audioEngine.value?.playNote(frequency + SCHWEBUNG_HZ, `${buttonId}-${index}+${SCHWEBUNG_HZ}`)
      audioEngine.value?.playNote(frequency - SCHWEBUNG_HZ, `${buttonId}-${index}-${SCHWEBUNG_HZ}`)
    })
  }

  function stopButtonNotes(rowIdx: number, btnIdx: number) {
    const buttonId = `${rowIdx}-${btnIdx}`

    for (let index = 0; index < MAX_NOTES_PER_BUTTON; index += 1) {
      audioEngine.value?.stopNote(`${buttonId}-${index}`)
      audioEngine.value?.stopNote(`${buttonId}-${index}+${SCHWEBUNG_HZ}`)
      audioEngine.value?.stopNote(`${buttonId}-${index}-${SCHWEBUNG_HZ}`)
    }
  }

  function activateButton(rowIdx: number, btnIdx: number) {
    const buttonId = `${rowIdx}-${btnIdx}`
    if (activeButtons.value.has(buttonId)) return

    const nextActiveButtons = new Set(activeButtons.value)
    nextActiveButtons.add(buttonId)
    activeButtons.value = nextActiveButtons
    playButtonNotes(rowIdx, btnIdx, direction.value)
  }

  function deactivateButton(rowIdx: number, btnIdx: number) {
    const buttonId = `${rowIdx}-${btnIdx}`
    if (!activeButtons.value.has(buttonId)) return

    const nextActiveButtons = new Set(activeButtons.value)
    nextActiveButtons.delete(buttonId)
    activeButtons.value = nextActiveButtons
    stopButtonNotes(rowIdx, btnIdx)
  }

  watch(direction, (nextDirection) => {
    activeButtons.value.forEach((buttonId) => {
      const parsed = parseButtonId(buttonId)
      if (!parsed) return
      const [rowIdx, btnIdx] = parsed
      stopButtonNotes(rowIdx, btnIdx)
      playButtonNotes(rowIdx, btnIdx, nextDirection)
    })
  })

  onMounted(() => {
    audioEngine.value = new AudioEngine()
  })

  onUnmounted(() => {
    audioEngine.value?.dispose()
    audioEngine.value = null
  })

  return {
    direction,
    activeButtons,
    activeMidiNotes,
    activateButton,
    deactivateButton,
  }
}
